import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  LAB,
  LAB_STARTERS,
  labHairline,
  labInk,
  labMuted,
  labSurface,
} from './vysorLabTheme';

type Starter = { id: string; label: string; description: string; prompt: string };

type Props = {
  mode: 'light' | 'dark';
  starters?: Starter[];
  onPick: (starter: Starter) => void;
  disabled?: boolean;
};

const VysorStarterGrid: React.FC<Props> = ({
  mode,
  starters = LAB_STARTERS,
  onPick,
  disabled,
}) => {
  const ink = labInk(mode);
  const muted = labMuted(mode);
  const hairline = labHairline(mode);
  const surface = labSurface(mode);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.1,
        width: '100%',
        maxWidth: LAB.column,
        mx: 'auto',
      }}
    >
      {starters.map((s, i) => (
        <ButtonBase
          key={s.id}
          disabled={disabled}
          onClick={() => onPick(s)}
          sx={{
            display: 'block',
            textAlign: 'left',
            px: 1.85,
            py: 1.6,
            borderRadius: `${LAB.radius.starter}px`,
            border: `1px solid ${hairline}`,
            bgcolor: surface,
            transition: 'border-color 150ms, background-color 150ms, transform 150ms',
            animation: `labStarterIn 420ms ease-out ${i * 50}ms both`,
            '@keyframes labStarterIn': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'none' },
            },
            '&:hover': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)',
              bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': { opacity: 0.5 },
          }}
        >
          <Typography
            sx={{
              fontFamily: LAB.fontBody,
              fontWeight: 600,
              fontSize: '0.92rem',
              color: ink,
              letterSpacing: '-0.01em',
            }}
          >
            {s.label}
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              fontFamily: LAB.fontBody,
              fontSize: '0.8rem',
              color: muted,
              lineHeight: 1.4,
            }}
          >
            {s.description}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  );
};

export default VysorStarterGrid;
